import React, { useState } from 'react';
import {
  Card,
  Typography,
  Upload,
  Button,
  Steps,
  Table,
  Space,
  Tag,
  message,
  Checkbox,
  Alert,
  Divider,
  Input,
  Modal,
  Form,
} from 'antd';
import { UploadOutlined, DownloadOutlined, CheckOutlined, SendOutlined, FileZipOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd/es/upload';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import openingStockService, { type OpeningStockBatch, type OpeningStockBatchLine } from '@/services/openingStock.service';
import DocumentUpload from '@/components/documents/DocumentUpload';
import type { ColumnsType } from 'antd/es/table';
import {
  getCriticalItemRowClassName,
  isItemCritical,
  ItemCodeWithCritical,
} from '@/components/inventory/criticalItemUi';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';

const { Title, Text, Paragraph } = Typography;

export const OpeningStockUploadPage: React.FC = () => {
  const [step, setStep] = useState(0);
  const [batch, setBatch] = useState<OpeningStockBatch | null>(null);
  const [allowSecond, setAllowSecond] = useState(false);
  const [uploadDepartmentId, setUploadDepartmentId] = useState<string | undefined>();
  const [zipResultOpen, setZipResultOpen] = useState(false);
  const [zipResult, setZipResult] = useState<{
    matched: Array<{ file: string; line_number: number; item_code: string; heat_number: string; dimension: string }>;
    unmatched: Array<{ file: string; reason: string }>;
    skipped: Array<{ file: string; reason: string }>;
    naming_hint: string;
  } | null>(null);
  const queryClient = useQueryClient();
  const { data: departments = [] } = useDepartmentsForSelection();

  const departmentLabel = (id?: string | null) => {
    if (!id) return '—';
    const d = departments.find((x) => x.id === id);
    return d ? `${d.department_code} - ${d.department_name}` : id;
  };

  const { data: batches = [], refetch: refetchList } = useQuery({
    queryKey: ['opening-stock-batches'],
    queryFn: () => openingStockService.listBatches(),
  });

  const validateMut = useMutation({
    mutationFn: () => openingStockService.validate(batch!.id),
    onSuccess: (b) => {
      setBatch(b);
      message.success('Validation complete');
      setStep(2);
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Validate failed'),
  });

  const postMut = useMutation({
    mutationFn: () => openingStockService.post(batch!.id, allowSecond),
    onSuccess: (b) => {
      setBatch(b);
      message.success('Opening stock posted');
      setStep(3);
      queryClient.invalidateQueries({ queryKey: ['opening-stock-batches'] });
      refetchList();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Post failed'),
  });

  const setMtcMut = useMutation({
    mutationFn: ({ lineId, mtcDocumentId }: { lineId: string; mtcDocumentId: string | null }) =>
      openingStockService.setLineMtc(batch!.id, lineId, mtcDocumentId),
    onSuccess: (b) => {
      setBatch(b);
      message.success('MTC linked');
      setStep(2);
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Failed to link MTC'),
  });

  const zipMut = useMutation({
    mutationFn: (file: File) => openingStockService.uploadMtcZip(batch!.id, file),
    onSuccess: (result) => {
      setBatch(result.batch);
      setZipResult(result);
      setZipResultOpen(true);
      setStep(2);
      message.success(`Matched ${result.matched.length} MTC file(s)`);
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'ZIP upload failed'),
  });

  const uploadProps: UploadProps = {
    maxCount: 1,
    accept: '.xlsx,.xls',
    showUploadList: true,
    disabled: !uploadDepartmentId,
    customRequest: async (options) => {
      const { file, onError, onSuccess } = options;
      if (!uploadDepartmentId) {
        message.error('Department is required');
        onError?.(new Error('Department is required'));
        return;
      }
      try {
        const b = await openingStockService.upload(file as File, uploadDepartmentId);
        setBatch(b);
        setStep(1);
        message.success('File uploaded');
        onSuccess?.(b);
        refetchList();
      } catch (e: any) {
        message.error(e?.response?.data?.message || e?.message || 'Upload failed');
        onError?.(e as Error);
      }
    },
  };

  const zipUploadProps: UploadProps = {
    maxCount: 1,
    accept: '.zip',
    showUploadList: false,
    disabled: !batch || batch.status === 'Posted' || batch.status === 'Cancelled' || zipMut.isPending,
    customRequest: async (options) => {
      const { file, onError, onSuccess } = options;
      try {
        const result = await zipMut.mutateAsync(file as File);
        onSuccess?.(result);
      } catch (e: any) {
        onError?.(e as Error);
      }
    },
  };

  const canEditMtc = batch && batch.status !== 'Posted' && batch.status !== 'Cancelled';

  const lineColumns: ColumnsType<OpeningStockBatchLine> = [
    { title: '#', dataIndex: 'line_number', width: 50 },
    {
      title: 'Item',
      dataIndex: 'item_code',
      width: 140,
      render: (code: string, row) => (
        <ItemCodeWithCritical code={code} critical={isItemCritical(row)} />
      ),
    },
    { title: 'Warehouse', dataIndex: 'warehouse_code', width: 100 },
    { title: 'Qty', dataIndex: 'quantity', width: 70 },
    { title: 'Rate', dataIndex: 'unit_rate', width: 70 },
    { title: 'Heat', dataIndex: 'heat_number', width: 100, ellipsis: true },
    { title: 'Dimension', dataIndex: 'dimension', width: 100, ellipsis: true },
    {
      title: 'MTC',
      key: 'mtc',
      width: 280,
      render: (_: unknown, row) => {
        const needsMtc = !!row.is_critical && !!row.mtc_required_on_receipt;
        const hasMtc = !!(row.mtc_document_id && String(row.mtc_document_id).trim());
        return (
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            {hasMtc ? (
              <Tag color="green">{row.mtc_document_number || row.mtc_document_id?.slice(0, 8)}</Tag>
            ) : needsMtc ? (
              <Tag color="orange">MTC required</Tag>
            ) : row.is_critical ? (
              <Text type="secondary">Optional</Text>
            ) : null}
            {canEditMtc && (needsMtc || row.is_critical) && (
              <Space wrap size={4}>
                <DocumentUpload
                  entityType="OpeningStockBatchLine"
                  entityId={row.id}
                  buttonText="Upload MTC"
                  onSuccess={(doc) => {
                    if (doc?.id) setMtcMut.mutate({ lineId: row.id, mtcDocumentId: doc.id });
                  }}
                />
                <Input
                  size="small"
                  placeholder="Doc # or UUID"
                  style={{ width: 140 }}
                  defaultValue={row.mtc_document_number || ''}
                  onPressEnter={(e) => {
                    const v = (e.target as HTMLInputElement).value.trim();
                    setMtcMut.mutate({ lineId: row.id, mtcDocumentId: v || null });
                  }}
                />
              </Space>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'row_status',
      width: 90,
      render: (s: string) => (
        <Tag color={s === 'Valid' ? 'green' : s === 'Error' ? 'red' : s === 'Posted' ? 'blue' : 'default'}>{s}</Tag>
      ),
    },
    {
      title: 'Messages',
      dataIndex: 'validation_errors',
      render: (errs: string[] | null) =>
        errs?.length ? (
          <ul style={{ margin: 0, paddingLeft: 16, maxWidth: 320 }}>
            {errs.map((e, i) => (
              <li key={i}>
                <Text type="secondary">{e}</Text>
              </li>
            ))}
          </ul>
        ) : null,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              Opening stock upload
            </Title>
            <Text type="secondary">
              Go-live utility: upload template, validate, attach MTC (single or ZIP bulk), then post. Critical items need
              heat number and dimension; if MTC is required on receipt, attach certificates before posting.
            </Text>
          </div>

          <Steps
            current={step}
            items={[
              { title: 'Upload' },
              { title: 'Validate' },
              { title: 'Review' },
              { title: 'Posted' },
            ]}
          />

          <Space wrap>
            <Form.Item
              label="Department"
              required
              style={{ marginBottom: 0 }}
              tooltip="Required before uploading opening stock"
            >
              <DepartmentSelect
                style={{ width: 280 }}
                value={uploadDepartmentId}
                onChange={(v) => setUploadDepartmentId(v as string | undefined)}
                placeholder="Select department"
              />
            </Form.Item>
            <Button icon={<DownloadOutlined />} onClick={() => openingStockService.downloadTemplate()}>
              Download template
            </Button>
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />} disabled={!uploadDepartmentId}>
                Upload Excel
              </Button>
            </Upload>
          </Space>

          {batch && step >= 1 && (
            <Alert
              type="info"
              showIcon
              message={`Batch ${batch.batch_number} — ${batch.lines?.length ?? 0} lines`}
              description={
                <Space wrap align="center">
                  <Text>Department: {departmentLabel(batch.department_id)}</Text>
                  {batch.status !== 'Posted' && batch.status !== 'Cancelled' && (
                    <DepartmentSelect
                      style={{ width: 280 }}
                      value={batch.department_id || undefined}
                      onChange={async (v) => {
                        try {
                          const updated = await openingStockService.updateBatch(batch.id, {
                            department_id: v as string,
                          });
                          setBatch(updated);
                          message.success('Department updated');
                          refetchList();
                        } catch (e: any) {
                          message.error(e?.response?.data?.message || e?.message || 'Update failed');
                        }
                      }}
                    />
                  )}
                </Space>
              }
            />
          )}

          {batch && step >= 1 && batch.status !== 'Posted' && (
            <Space wrap>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                loading={validateMut.isPending}
                onClick={() => validateMut.mutate()}
                disabled={step < 1}
              >
                Run validation
              </Button>
            </Space>
          )}

          {batch && step >= 2 && (
            <>
              <Alert
                type="warning"
                showIcon
                message="Critical items & MTC"
                description={
                  <div>
                    <Paragraph style={{ marginBottom: 8 }}>
                      Heat-mandatory items need <strong>heat_number</strong> and <strong>dimension</strong> in Excel.
                      For bulk MTC, zip PDFs named <code>ItemCode__HeatNumber__Dimension.pdf</code> (example:{' '}
                      <code>NTES-2001__H12345__12MM.pdf</code>), then use <strong>Upload MTC ZIP</strong>.
                    </Paragraph>
                    {canEditMtc && (
                      <Upload {...zipUploadProps}>
                        <Button icon={<FileZipOutlined />} loading={zipMut.isPending}>
                          Upload MTC ZIP
                        </Button>
                      </Upload>
                    )}
                  </div>
                }
              />
              <Table<OpeningStockBatchLine>
                rowKey="id"
                size="small"
                scroll={{ x: 1200 }}
                pagination={{ defaultPageSize: 15, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                columns={lineColumns}
                dataSource={batch.lines || []}
                rowClassName={(r) => getCriticalItemRowClassName(r)}
              />
              {batch.status !== 'Posted' && (
                <>
                  <Checkbox checked={allowSecond} onChange={(e) => setAllowSecond(e.target.checked)}>
                    Allow posting when company already has a posted opening batch (admin override)
                  </Checkbox>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    loading={postMut.isPending}
                    onClick={() => postMut.mutate()}
                    disabled={!batch.lines?.some((l) => l.row_status === 'Valid')}
                  >
                    Post opening stock
                  </Button>
                </>
              )}
            </>
          )}

          <Divider />
          <Title level={5}>Recent batches</Title>
          <Table<OpeningStockBatch>
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={batches.slice(0, 10)}
            columns={[
              { title: 'Batch', dataIndex: 'batch_number' },
              { title: 'Status', dataIndex: 'status' },
              {
                title: 'Department',
                dataIndex: 'department_id',
                render: (id: string | null | undefined) => departmentLabel(id),
              },
              { title: 'File', dataIndex: 'file_name' },
              {
                title: '',
                render: (_: unknown, r) => (
                  <Button
                    type="link"
                    onClick={async () => {
                      const b = await openingStockService.getBatch(r.id);
                      setBatch(b);
                      setStep(b.status === 'Posted' ? 3 : b.status === 'Validated' ? 2 : 1);
                    }}
                  >
                    Open
                  </Button>
                ),
              },
            ]}
          />
        </Space>
      </Card>

      <Modal
        title="MTC ZIP results"
        open={zipResultOpen}
        onCancel={() => setZipResultOpen(false)}
        onOk={() => setZipResultOpen(false)}
        width={720}
      >
        {zipResult && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Text type="secondary">{zipResult.naming_hint}</Text>
            <Alert
              type="success"
              showIcon
              message={`Matched ${zipResult.matched.length}`}
              description={
                zipResult.matched.length ? (
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {zipResult.matched.slice(0, 50).map((m, i) => (
                      <li key={i}>
                        {m.file} → line {m.line_number} ({m.item_code} / {m.heat_number} / {m.dimension})
                      </li>
                    ))}
                    {zipResult.matched.length > 50 ? <li>…and more</li> : null}
                  </ul>
                ) : (
                  'No files matched'
                )
              }
            />
            {!!zipResult.unmatched.length && (
              <Alert
                type="error"
                showIcon
                message={`Unmatched ${zipResult.unmatched.length}`}
                description={
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {zipResult.unmatched.slice(0, 50).map((u, i) => (
                      <li key={i}>
                        {u.file}: {u.reason}
                      </li>
                    ))}
                  </ul>
                }
              />
            )}
            {!!zipResult.skipped.length && (
              <Alert
                type="info"
                showIcon
                message={`Skipped ${zipResult.skipped.length}`}
                description={
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {zipResult.skipped.slice(0, 20).map((s, i) => (
                      <li key={i}>
                        {s.file}: {s.reason}
                      </li>
                    ))}
                  </ul>
                }
              />
            )}
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default OpeningStockUploadPage;
